"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PromotionalCardConfig } from "./types";
import { usePromotionalCards } from "./use-cards";
const ROTATION_INDEX_KEY = "settlin_promo_card_rotation_index";

/**
 * Get and increment the rotation index from sessionStorage
 */
function getNextRotationIndex(totalCards: number): number {
  if (typeof window === "undefined" || totalCards === 0) return 0;
  try {
    const stored = sessionStorage.getItem(ROTATION_INDEX_KEY);
    const currentIndex = stored ? parseInt(stored, 10) : 0;
    const nextIndex = (currentIndex + 1) % totalCards;
    sessionStorage.setItem(ROTATION_INDEX_KEY, String(nextIndex));
    return currentIndex;
  } catch {
    return 0;
  }
}

/**
 * Hook to get cards in round-robin rotation for lists.
 * Each call to getNextCard returns the next card in sequence.
 * Cards cycle through all eligible cards before repeating.
 */
export function useRotatingCards(pageType) {
  const $ = _c(9);
  const {
    potentialCards
  } = usePromotionalCards(pageType);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [shuffledCards, setShuffledCards] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== potentialCards) {
    t1 = () => {
      if (potentialCards.length === 0) {
        setShuffledCards([]);
        return;
      }
      const cards = [...potentialCards];
      for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const [t3, t4] = [cards[j], cards[i]];
        cards[i] = t3;
        cards[j] = t4;
      }
      setShuffledCards(cards);
    };
    t2 = [potentialCards];
    $[1] = potentialCards;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const localIndexRef = useRef(0);
  let t3;
  if ($[4] !== shuffledCards) {
    t3 = () => {
      if (shuffledCards.length === 0) {
        return;
      }
      const globalIndex = getNextRotationIndex(shuffledCards.length);
      const combinedIndex = (globalIndex + localIndexRef.current) % shuffledCards.length;
      localIndexRef.current = localIndexRef.current + 1;
      return shuffledCards[combinedIndex];
    };
    $[4] = shuffledCards;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getNextCard = t3;
  let t4;
  if ($[6] !== getNextCard || $[7] !== shuffledCards) {
    t4 = {
      getNextCard,
      totalCards: shuffledCards.length,
      cards: shuffledCards
    };
    $[6] = getNextCard;
    $[7] = shuffledCards;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/**
 * Hook to get a single random card for SUP/project pages.
 * Uses useMemo to ensure card is stable per page load.
 */
export function useRandomCard(pageType) {
  const $ = _c(6);
  const {
    potentialCards,
    getRandomCards
  } = usePromotionalCards(pageType);
  const [clientCard, setClientCard] = useState(undefined);
  let t0;
  let t1;
  if ($[0] !== getRandomCards || $[1] !== potentialCards) {
    t0 = () => {
      if (potentialCards.length > 0) {
        const selected = getRandomCards(1, potentialCards);
        setClientCard(selected[0]);
      }
    };
    t1 = [potentialCards, getRandomCards];
    $[0] = getRandomCards;
    $[1] = potentialCards;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== clientCard) {
    t2 = {
      card: clientCard
    };
    $[4] = clientCard;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Hook to get a random position index for inserting promotional cards.
 * Returns a stable position per page load within the given range.
 */
export function useRandomPosition(maxPositions) {
  const $ = _c(3);
  const [position, setPosition] = useState(0);
  let t0;
  let t1;
  if ($[0] !== maxPositions) {
    t0 = () => {
      if (maxPositions > 0) {
        setPosition(Math.floor(Math.random() * maxPositions));
      }
    };
    t1 = [maxPositions];
    $[0] = maxPositions;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  bb0: {
    if (maxPositions <= 0) {
      t2 = 0;
      break bb0;
    }
    t2 = position;
  }
  return t2;
}
interface PromotionalCardRendererProps {
  card: PromotionalCardConfig;
  className?: string;
}

/**
 * Component to render a promotional card from config
 */
export function PromotionalCardRenderer(t0) {
  const $ = _c(3);
  const {
    card,
    className
  } = t0;
  const CardComponent = card.component;
  let t1;
  if ($[0] !== CardComponent || $[1] !== className) {
    t1 = <CardComponent className={className} />;
    $[0] = CardComponent;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
interface PromotionalCardsSectionProps {
  pageType: "sup" | "project";
  className?: string;
  /** Which slot this is (0-indexed). Card renders only if this matches the random position */
  slot?: number;
  /** Total number of slots available. Used to calculate random position */
  totalSlots?: number;
}

/**
 * A section that displays a single random promotional card for SUP and Project pages.
 * Uses section-matching padding (px-2 @4xl:px-0) by default.
 *
 * When slot and totalSlots are provided, the card only renders if the random position
 * matches this slot. This allows placing multiple PromotionalCardsSections throughout
 * the page with only one actually rendering.
 */
export function PromotionalCardsSection(t0) {
  const $ = _c(7);
  const {
    pageType,
    className,
    slot,
    totalSlots
  } = t0;
  const {
    card
  } = useRandomCard(pageType);
  const randomPosition = useRandomPosition(totalSlots ?? 1);
  if (slot !== undefined && totalSlots !== undefined && slot !== randomPosition) {
    return null;
  }
  if (!card) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("px-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== card) {
    t2 = <PromotionalCardRenderer card={card} />;
    $[2] = card;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}